import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  ArrowLeft, 
  Calendar, 
  Users, 
  CheckCircle,
  Circle,
  FileText,
  Trash2,
  RefreshCw,
  Loader2,
  AlertCircle,
  Target,
  Lightbulb,
  Download,
  Share2,
  Mail,
  FileDown,
  X,
  Lock,
  PlayCircle,
  ChevronDown,
  ChevronUp
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { es } from "date-fns/locale";
import { useAuth } from "@/context/AuthContext";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusLabels = {
  pending: "Pendiente",
  recording: "Grabando",
  transcribing: "Transcribiendo audio...",
  processing: "Generando minutas con IA...",
  completed: "Completado",
  error: "Error"
};

const ProcessingState = ({ status, errorMessage, onReprocess, reprocessing }) => (
  <div className="min-h-[60vh] flex items-center justify-center">
    <div className="text-center max-w-md">
      {status === "error" ? (
        <>
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-red-100 flex items-center justify-center">
            <AlertCircle className="w-10 h-10 text-red-500" />
          </div>
          <h2 className="text-2xl font-bold mb-2">Error en el procesamiento</h2>
          <p className="text-muted-foreground mb-6">
            {errorMessage ? (
              <>
                {errorMessage.includes('502') || errorMessage.includes('BadGateway') ? (
                  <>
                    Error al transcribir el audio: El servicio de transcripción está temporalmente sobrecargado.
                    <br /><br />
                    <span className="text-sm">Por favor, intenta reprocesar el audio en unos minutos.</span>
                  </>
                ) : (
                  errorMessage
                )}
              </>
            ) : (
              "Hubo un error al procesar el audio."
            )}
          </p>
          {onReprocess && (
            <Button 
              onClick={onReprocess}
              disabled={reprocessing}
              className="bg-orange-500 hover:bg-orange-600 text-white"
            >
              {reprocessing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Reprocesando...
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Reprocesar Audio
                </>
              )}
            </Button>
          )}
        </>
      ) : (
        <>
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-orange-100 flex items-center justify-center">
            <Loader2 className="w-10 h-10 text-orange-500 animate-spin" />
          </div>
          <h2 className="text-2xl font-bold mb-2">{statusLabels[status]}</h2>
          <p className="text-muted-foreground">
            {status === "transcribing" 
              ? "Convirtiendo el audio a texto con inteligencia artificial..."
              : "Analizando la transcripción y extrayendo información clave..."
            }
          </p>
        </>
      )}
    </div>
  </div>
);

const ShareDialog = ({ open, onClose, meeting }) => {
  const [emails, setEmails] = useState([""]);
  const [sections, setSections] = useState({
    summary: true,
    participants: true,
    action_items: true,
    key_decisions: true,
    transcript: false
  });
  const [sending, setSending] = useState(false);

  const addEmailField = () => {
    if (emails.length < 10) {
      setEmails([...emails, ""]);
    }
  };

  const removeEmailField = (index) => {
    if (emails.length > 1) {
      setEmails(emails.filter((_, i) => i !== index));
    }
  };

  const updateEmail = (index, value) => {
    const newEmails = [...emails];
    newEmails[index] = value;
    setEmails(newEmails);
  };

  const handleShare = async () => {
    if (!meeting) return;
    
    // Filter valid emails
    const validEmails = emails.filter(e => e.trim() && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e.trim()));
    
    if (validEmails.length === 0) {
      toast.error("Ingresa al menos un correo electrónico válido");
      return;
    }
    
    const selectedSections = Object.entries(sections)
      .filter(([_, v]) => v)
      .map(([k]) => k);
    
    if (selectedSections.length === 0) {
      toast.error("Selecciona al menos una sección para compartir");
      return;
    }
    
    setSending(true);
    try {
      const response = await axios.post(`${API}/meetings/${meeting.id}/share`, {
        recipient_emails: validEmails,
        sections: selectedSections
      });
      toast.success(response.data.message || `Minuta enviada a ${validEmails.length} destinatario(s)`);
      onClose();
      setEmails([""]);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al enviar el correo");
    } finally {
      setSending(false);
    }
  };

  if (!meeting) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-orange-500" />
            Compartir por Email
          </DialogTitle>
          <DialogDescription>
            Envía un resumen de la minuta a uno o varios destinatarios
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Destinatarios</Label>
              <Button 
                type="button" 
                variant="ghost" 
                size="sm" 
                onClick={addEmailField}
                disabled={emails.length >= 10}
                className="text-xs h-7"
              >
                + Agregar otro
              </Button>
            </div>
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {emails.map((email, index) => (
                <div key={index} className="flex items-center gap-2">
                  <Input
                    type="email"
                    placeholder="correo@ejemplo.com"
                    value={email}
                    onChange={(e) => updateEmail(index, e.target.value)}
                    className="flex-1"
                    data-testid={`share-email-input-${index}`}
                  />
                  {emails.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeEmailField(index)}
                      className="h-9 w-9 text-stone-400 hover:text-red-500"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Puedes agregar hasta 10 destinatarios
            </p>
          </div>
          
          <div>
            <Label className="mb-3 block">Secciones a incluir</Label>
            <div className="space-y-2">
              {[
                { key: "summary", label: "Resumen ejecutivo" },
                { key: "participants", label: "Participantes" },
                { key: "action_items", label: "Tareas" },
                { key: "key_decisions", label: "Decisiones clave" },
                { key: "transcript", label: "Transcripción" }
              ].map(({ key, label }) => (
                <div key={key} className="flex items-center gap-2">
                  <Checkbox
                    id={`share-${key}`}
                    checked={sections[key]}
                    onCheckedChange={(checked) => setSections(prev => ({ ...prev, [key]: checked }))}
                    data-testid={`share-checkbox-${key}`}
                  />
                  <Label htmlFor={`share-${key}`} className="font-normal cursor-pointer">
                    {label}
                  </Label>
                </div>
              ))}
            </div>
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button 
            onClick={handleShare} 
            disabled={sending}
            className="bg-orange-500 hover:bg-orange-600"
            data-testid="share-send-btn"
          >
            {sending ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Enviando...
              </>
            ) : (
              <>
                <Mail className="w-4 h-4 mr-2" />
                Enviar
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const MeetingDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { canDownload, canShare, canCompleteTasks, getPlanName } = useAuth();
  const [meeting, setMeeting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [reprocessing, setReprocessing] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [isParticipantsOpen, setIsParticipantsOpen] = useState(true);
  const [isTasksOpen, setIsTasksOpen] = useState(true);
  const [isDecisionsOpen, setIsDecisionsOpen] = useState(true);

  const userCanDownload = canDownload();
  const userCanShare = canShare();
  const userCanCompleteTasks = canCompleteTasks();
  const planName = getPlanName();

  useEffect(() => {
    fetchMeeting();
  }, [id]);

  useEffect(() => {
    // Poll for updates if processing
    if (meeting && ["transcribing", "processing"].includes(meeting.status)) {
      const interval = setInterval(fetchMeeting, 3000);
      return () => clearInterval(interval);
    }
  }, [meeting?.status]);

  const fetchMeeting = async () => {
    try {
      const response = await axios.get(`${API}/meetings/${id}`);
      setMeeting(response.data);
    } catch (err) {
      // console.error("Error fetching meeting:", err);
      toast.error("No se pudo cargar la reunión");
      navigate("/");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/meetings/${id}`);
      toast.success("Reunión eliminada");
      navigate("/");
    } catch (err) {
      // console.error("Error deleting meeting:", err);
      toast.error("Error al eliminar la reunión");
    } finally {
      setDeleting(false);
    }
  };

  const handleReprocess = async () => {
    setReprocessing(true);
    try {
      // Try emergency endpoint first if this meeting has persistent errors
      const isEmergency = meeting.error_message?.includes('presupuesto') || meeting.error_message?.includes('budget');
      
      if (isEmergency) {
        // console.log('Using emergency endpoint...');
        await axios.post(`${API}/api/meetings/${id}/emergency-reprocess`, {}, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        toast.success("🚨 Reprocesando con sistema de emergencia (OpenAI directo)...");
      } else {
        // Use regular reprocess-audio endpoint
        await axios.post(`${API}/api/meetings/${id}/reprocess-audio`, {}, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        toast.success("Reprocesando audio desde el respaldo...");
      }
      
      // Poll for status updates
      const pollInterval = setInterval(() => {
        fetchMeeting();
      }, 3000); // Check every 3 seconds
      
      // Stop polling after 2 minutes
      setTimeout(() => {
        clearInterval(pollInterval);
      }, 120000);
      
    } catch (err) {
      // console.error("Error reprocessing meeting:", err);
      toast.error(err.response?.data?.detail || "Error al reprocesar");
    } finally {
      setReprocessing(false);
    }
  };

  const toggleActionItem = async (itemId) => {
    if (!userCanCompleteTasks) {
      toast.error(`Tu plan ${planName} no permite marcar tareas. Actualiza tu plan.`);
      return;
    }
    try {
      await axios.put(`${API}/meetings/${id}/action-items/${itemId}`);
      fetchMeeting();
    } catch (err) {
      // console.error("Error toggling action item:", err);
      toast.error(err.response?.data?.detail || "Error al actualizar tarea");
    }
  };

  const updateTaskStatus = async (itemId, newStatus) => {
    if (!userCanCompleteTasks) {
      toast.error(`Tu plan ${planName} no permite gestionar tareas. Actualiza tu plan.`);
      return;
    }
    try {
      await axios.patch(`${API}/meetings/${id}/action-items/${itemId}/status`, {
        status: newStatus
      });
      
      const statusMessages = {
        'pending': 'Tarea marcada como pendiente',
        'in_progress': 'Tarea en proceso',
        'completed': '¡Tarea completada!'
      };
      
      toast.success(statusMessages[newStatus]);
      fetchMeeting();
    } catch (err) {
      // console.error("Error updating task status:", err);
      toast.error(err.response?.data?.detail || "Error al actualizar tarea");
    }
  };

  const getTaskStatusIcon = (item) => {
    if (item.status === 'in_progress') {
      return <PlayCircle className="w-5 h-5 text-blue-500 shrink-0 mt-0.5" />;
    } else if (item.completed || item.status === 'completed') {
      return <CheckCircle className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />;
    }
    return <Circle className={`w-5 h-5 shrink-0 mt-0.5 ${userCanCompleteTasks ? 'text-stone-400' : 'text-stone-300'}`} />;
  };

  const getTaskStatusBg = (item) => {
    if (item.status === 'in_progress') {
      return 'bg-blue-50 border-blue-200';
    } else if (item.completed || item.status === 'completed') {
      return 'bg-emerald-50 border-emerald-200';
    }
    return 'bg-stone-50 hover:bg-stone-100';
  };

  const handleDownload = async (format, sections = "all") => {
    if (!userCanDownload) {
      toast.error(`Tu plan ${planName} no permite descargar. Actualiza tu plan.`);
      return;
    }
    try {
      const response = await axios.get(`${API}/meetings/${id}/download`, {
        params: { format, sections },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `minuta_${meeting.title.replace(/\s+/g, '_')}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Descargando archivo ${format.toUpperCase()}`);
    } catch (err) {
      // console.error("Error downloading:", err);
      const errorMsg = err.response?.data?.detail || "Error al descargar el archivo";
      toast.error(errorMsg);
    }
  };

  const handleDownloadAudio = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API}/meetings/${meeting.id}/download-audio`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        if (response.status === 404) {
          toast.error("Audio no disponible para esta reunión");
        } else {
          toast.error("Error al descargar audio");
        }
        return;
      }

      // Get filename from header or use default
      const contentDisposition = response.headers.get('content-disposition');
      let filename = 'audio.mp3';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?(.+)"?/);
        if (match) filename = match[1];
      }

      // Download the file
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success("Audio descargado correctamente");
    } catch (err) {
      // console.error("Error downloading audio:", err);
      toast.error("Error al descargar audio");
    }
  };


  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  if (!meeting) {
    return (
      <div className="text-center py-20">
        <h2 className="text-2xl font-bold mb-2">Reunión no encontrada</h2>
        <Link to="/">
          <Button>Volver al inicio</Button>
        </Link>
      </div>
    );
  }

  const isProcessing = ["transcribing", "processing"].includes(meeting.status);

  // Clean transcript text - extract only the text from segments
  const getCleanTranscript = () => {
    // First try raw_transcript which should be clean text
    if (meeting.raw_transcript && typeof meeting.raw_transcript === 'string') {
      return meeting.raw_transcript;
    }
    
    // Try to parse transcript array
    if (meeting.transcript && Array.isArray(meeting.transcript) && meeting.transcript.length > 0) {
      const cleanText = meeting.transcript
        .map(seg => {
          // Handle string segments
          if (typeof seg === 'string') {
            // Check if it's a JSON string
            try {
              const parsed = JSON.parse(seg);
              if (parsed && typeof parsed.text === 'string') {
                return parsed.text.trim();
              }
            } catch {
              return seg.trim();
            }
          }
          // Handle object segments
          if (seg && typeof seg === 'object') {
            if (typeof seg.text === 'string') {
              return seg.text.trim();
            }
          }
          return '';
        })
        .filter(text => text.length > 0)
        .join(" ");
      
      if (cleanText) return cleanText;
    }
    
    return "No hay transcripción disponible.";
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <div className="flex items-start justify-between gap-4 mb-4">
          <div className="flex items-center gap-4">
            <Link to="/">
              <Button variant="ghost" size="icon" data-testid="back-btn">
                <ArrowLeft className="w-5 h-5" />
              </Button>
            </Link>
            <div>
              <h1 className="text-3xl font-bold" data-testid="meeting-title">
                {meeting.title}
              </h1>
              <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  {format(parseISO(meeting.date), "d MMMM yyyy, HH:mm", { locale: es })}
                </span>
                {meeting.participants?.length > 0 && (
                  <span className="flex items-center gap-1">
                    <Users className="w-4 h-4" />
                    {meeting.participants.length} participantes
                  </span>
                )}
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {meeting.status === "completed" && (
              <>
                {/* Download Dropdown */}
                <DropdownMenu modal={false}>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      data-testid="download-btn"
                      className={!userCanDownload ? "opacity-60" : ""}
                    >
                      {!userCanDownload ? (
                        <Lock className="w-4 h-4 mr-2" />
                      ) : (
                        <Download className="w-4 h-4 mr-2" />
                      )}
                      Descargar
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    {!userCanDownload ? (
                      <DropdownMenuItem onSelect={() => navigate("/pricing")}>
                        <div className="flex flex-col items-center w-full py-2">
                          <Lock className="w-6 h-6 mb-2 text-stone-400" />
                          <p className="text-sm text-stone-600 mb-2 text-center">Función no disponible en plan {planName}</p>
                          <span className="text-xs text-orange-600 font-medium">Actualizar plan →</span>
                        </div>
                      </DropdownMenuItem>
                    ) : (
                      <>
                        <DropdownMenuItem onSelect={() => handleDownload("docx")} data-testid="download-docx">
                          <FileDown className="w-4 h-4 mr-2" />
                          Word (.docx)
                        </DropdownMenuItem>
                        <DropdownMenuItem onSelect={() => handleDownload("txt")} data-testid="download-txt">
                          <FileText className="w-4 h-4 mr-2" />
                          Texto (.txt)
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onSelect={() => handleDownload("docx", "summary")} data-testid="download-summary">
                          Solo resumen
                        </DropdownMenuItem>
                        <DropdownMenuItem onSelect={() => handleDownload("docx", "transcript")} data-testid="download-transcript">
                          Solo transcripción
                        </DropdownMenuItem>
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
                
                {/* Download Audio Button */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDownloadAudio}
                  data-testid="download-audio-btn"
                  title="Descargar audio original de la reunión"
                >
                  <PlayCircle className="w-4 h-4 mr-2" />
                  Audio
                </Button>
                
                {/* Share Button */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    if (!userCanShare) {
                      toast.error(`Tu plan ${planName} no permite compartir. Actualiza tu plan.`);
                      return;
                    }
                    setTimeout(() => setShareOpen(true), 0);
                  }}
                  data-testid="share-btn"
                  className={!userCanShare ? "opacity-60" : ""}
                >
                  {!userCanShare ? (
                    <Lock className="w-4 h-4 mr-2" />
                  ) : (
                    <Share2 className="w-4 h-4 mr-2" />
                  )}
                  Compartir
                </Button>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleReprocess}
                  disabled={reprocessing}
                  data-testid="reprocess-btn"
                >
                  {reprocessing ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4 mr-2" />
                  )}
                  Reprocesar
                </Button>
              </>
            )}
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" size="sm" data-testid="delete-meeting-btn">
                  <Trash2 className="w-4 h-4 mr-2" />
                  Eliminar
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>¿Eliminar reunión?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Esta acción no se puede deshacer. Se eliminará permanentemente esta reunión y toda su información.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleDelete}
                    disabled={deleting}
                    className="bg-red-500 hover:bg-red-600"
                    data-testid="confirm-delete-btn"
                  >
                    {deleting ? "Eliminando..." : "Eliminar"}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>

        {meeting.description && (
          <p className="text-muted-foreground ml-14">{meeting.description}</p>
        )}
      </motion.div>

      {/* Processing State */}
      {(isProcessing || meeting.status === "error") && (
        <ProcessingState 
          status={meeting.status} 
          errorMessage={meeting.error_message}
          onReprocess={handleReprocess}
          reprocessing={reprocessing}
        />
      )}

      {/* Completed Content */}
      {meeting.status === "completed" && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="grid grid-cols-1 lg:grid-cols-12 gap-6"
        >
          {/* Main Content */}
          <div className="lg:col-span-7 space-y-6">
            <Tabs defaultValue="summary" className="w-full">
              <TabsList className="w-full justify-start">
                <TabsTrigger value="summary" data-testid="tab-summary">
                  <FileText className="w-4 h-4 mr-2" />
                  Resumen
                </TabsTrigger>
                <TabsTrigger value="transcript" data-testid="tab-transcript">
                  <FileText className="w-4 h-4 mr-2" />
                  Transcripción
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="summary" className="mt-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Lightbulb className="w-5 h-5 text-orange-500" />
                      Resumen Ejecutivo
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-foreground/90 leading-relaxed whitespace-pre-wrap" data-testid="meeting-summary">
                      {meeting.summary || "No se generó un resumen para esta reunión."}
                    </p>
                  </CardContent>
                </Card>
              </TabsContent>
              
              <TabsContent value="transcript" className="mt-4">
                <Card>
                  <CardHeader>
                    <CardTitle>Transcripción Completa</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ScrollArea className="h-[500px] pr-4">
                      <p className="text-foreground/90 leading-relaxed whitespace-pre-wrap" data-testid="clean-transcript">
                        {getCleanTranscript() || "No hay transcripción disponible."}
                      </p>
                    </ScrollArea>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-5 space-y-6">
            {/* Participants */}
            {meeting.participants?.length > 0 && (
              <Card>
                <CardHeader 
                  className="cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => setIsParticipantsOpen(!isParticipantsOpen)}
                >
                  <CardTitle className="flex items-center justify-between text-lg">
                    <div className="flex items-center gap-2">
                      <Users className="w-5 h-5 text-blue-500" />
                      Participantes ({meeting.participants.length})
                    </div>
                    {isParticipantsOpen ? (
                      <ChevronUp className="w-5 h-5 text-gray-500" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-500" />
                    )}
                  </CardTitle>
                </CardHeader>
                {isParticipantsOpen && (
                  <CardContent>
                    <div className="space-y-3" data-testid="participants-list">
                      {meeting.participants.map((participant, idx) => (
                        <div key={participant.id || idx} className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-stone-200 flex items-center justify-center text-sm font-medium">
                            {participant.name.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <div className="font-medium">{participant.name}</div>
                            {participant.role && (
                              <div className="text-xs text-muted-foreground">{participant.role}</div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                )}
              </Card>
            )}

            {/* Action Items */}
            <Card>
              <CardHeader 
                className="cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => setIsTasksOpen(!isTasksOpen)}
              >
                <CardTitle className="flex items-center justify-between text-lg">
                  <div className="flex items-center gap-2">
                    <Target className="w-5 h-5 text-orange-500" />
                    Tareas ({meeting.action_items?.length || 0})
                    {!userCanCompleteTasks && (
                      <Badge variant="secondary" className="text-xs">
                        <Lock className="w-3 h-3 mr-1" />
                        Solo lectura
                      </Badge>
                    )}
                  </div>
                  {isTasksOpen ? (
                    <ChevronUp className="w-5 h-5 text-gray-500" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-500" />
                  )}
                </CardTitle>
              </CardHeader>
              {isTasksOpen && (
                <CardContent>
                {!userCanCompleteTasks && meeting.action_items?.length > 0 && (
                  <div className="mb-3 p-2 bg-amber-50 rounded-lg text-xs text-amber-700 flex items-center gap-2">
                    <Lock className="w-3 h-3" />
                    <span>Actualiza a plan Básico o Pro para gestionar tareas</span>
                  </div>
                )}
                {meeting.action_items?.length > 0 ? (
                  <div className="space-y-3" data-testid="action-items-list">
                    {meeting.action_items.map((item, idx) => (
                      <div 
                        key={item.id || idx} 
                        className={`flex items-start gap-3 p-3 rounded-lg border transition-colors ${getTaskStatusBg(item)}`}
                        data-testid={`action-item-${idx}`}
                      >
                        {getTaskStatusIcon(item)}
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm ${(item.completed || item.status === 'completed') ? "line-through text-muted-foreground" : ""}`}>
                            {item.description}
                          </p>
                          {item.assignee && (
                            <p className="text-xs text-muted-foreground mt-1">
                              Asignado a: {item.assignee}
                            </p>
                          )}
                          {item.status === 'in_progress' && (
                            <Badge variant="secondary" className="mt-2 text-xs bg-blue-100 text-blue-700">
                              En proceso
                            </Badge>
                          )}
                          {(item.completed || item.status === 'completed') && (
                            <Badge variant="secondary" className="mt-2 text-xs bg-emerald-100 text-emerald-700">
                              Completada
                            </Badge>
                          )}
                        </div>
                        {userCanCompleteTasks && (
                          <div className="flex flex-col gap-1 shrink-0">
                            {/* Botones para tareas pendientes */}
                            {(!item.status || item.status === 'pending') && (
                              <>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-7 px-2 text-xs text-blue-600 hover:text-blue-700 hover:bg-blue-100"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    updateTaskStatus(item.id, 'in_progress');
                                  }}
                                >
                                  <PlayCircle className="w-3 h-3 mr-1" />
                                  Iniciar
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-7 px-2 text-xs text-emerald-600 hover:text-emerald-700 hover:bg-emerald-100"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    updateTaskStatus(item.id, 'completed');
                                  }}
                                >
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                  Completar
                                </Button>
                              </>
                            )}
                            {/* Botones para tareas en proceso */}
                            {item.status === 'in_progress' && (
                              <>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-7 px-2 text-xs text-stone-600 hover:text-stone-700 hover:bg-stone-200"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    updateTaskStatus(item.id, 'pending');
                                  }}
                                >
                                  <Circle className="w-3 h-3 mr-1" />
                                  Pendiente
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-7 px-2 text-xs text-emerald-600 hover:text-emerald-700 hover:bg-emerald-100"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    updateTaskStatus(item.id, 'completed');
                                  }}
                                >
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                  Completar
                                </Button>
                              </>
                            )}
                            {/* Botones para tareas completadas */}
                            {(item.completed || item.status === 'completed') && (
                              <>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-7 px-2 text-xs text-stone-600 hover:text-stone-700 hover:bg-stone-200"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    updateTaskStatus(item.id, 'pending');
                                  }}
                                >
                                  <Circle className="w-3 h-3 mr-1" />
                                  Pendiente
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-7 px-2 text-xs text-blue-600 hover:text-blue-700 hover:bg-blue-100"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    updateTaskStatus(item.id, 'in_progress');
                                  }}
                                >
                                  <PlayCircle className="w-3 h-3 mr-1" />
                                  En proceso
                                </Button>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-sm">
                    No se identificaron tareas en esta reunión.
                  </p>
                )}
              </CardContent>
              )}
            </Card>

            {/* Key Decisions */}
            {meeting.key_decisions?.length > 0 && (
              <Card>
                <CardHeader 
                  className="cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => setIsDecisionsOpen(!isDecisionsOpen)}
                >
                  <CardTitle className="flex items-center justify-between text-lg">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-emerald-500" />
                      Decisiones Clave ({meeting.key_decisions.length})
                    </div>
                    {isDecisionsOpen ? (
                      <ChevronUp className="w-5 h-5 text-gray-500" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-500" />
                    )}
                  </CardTitle>
                </CardHeader>
                {isDecisionsOpen && (
                  <CardContent>
                  <ul className="space-y-2" data-testid="key-decisions-list">
                    {meeting.key_decisions.map((decision, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-2 shrink-0" />
                        {decision}
                      </li>
                    ))}
                  </ul>
                </CardContent>
                )}
              </Card>
            )}
          </div>
        </motion.div>
      )}

      {/* Pending State */}
      {meeting.status === "pending" && (
        <div className="text-center py-20">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-stone-100 flex items-center justify-center">
            <FileText className="w-10 h-10 text-stone-400" />
          </div>
          <h2 className="text-2xl font-bold mb-2">Reunión pendiente</h2>
          <p className="text-muted-foreground mb-4">
            Esta reunión aún no tiene audio procesado.
          </p>
        </div>
      )}

      {/* Share Dialog */}
      <ShareDialog 
        open={shareOpen && meeting !== null} 
        onClose={() => setShareOpen(false)} 
        meeting={meeting} 
      />
    </div>
  );
};

export default MeetingDetail;
